import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import {
  ChapterCard,
  ChipBadge,
  DashboardGrid,
  IStyles,
  Loader,
  NoContentCard,
  getLocalStorage,
  getMediaBasePath,
  pxToRem,
  pxTovW,
  useCommonServiceClientContext,
} from '@geneo2-web/shared-ui';
import { ProfileRolesEnum } from '@protos/user_management/ums.db_pb';
import { FetchSubjectChapterInfoV2 } from '@protos/content_management/content.common.apis_pb';
import { useDeserifiedSelector } from '../../../reduxStore/reduxHooks';
import { useGlobalContext } from '../../../app/Context/GlobalContextProvider';
import {
  HOMEWORK_MANAGE,
  LEARNING_HOMEWORK_CREATE,
} from '../../../routeHandling/RoutesNomenclature';

// Duplicated from Assessment/Homework/HwChapterSelection (demo only).
const styles: IStyles = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    boxSizing: 'border-box',
    padding: {
      xs: pxToRem(20),
      md: `${pxTovW(50)} ${pxTovW(40)}`,
      lg: `${pxTovW(50)} ${pxTovW(240)}`,
    },
    gap: { xs: pxToRem(20), md: pxTovW(40) },
  },
  textWithBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: { xs: pxToRem(10), md: pxTovW(10) },
  },
};

export default function LearningHwChapterSelection() {
  const navigate = useNavigate();
  const { subject_id } = useParams();
  const { setSelectedFunction } = useGlobalContext();
  const teacher_id = getLocalStorage('userId');
  const class_subject_info = useDeserifiedSelector(
    (state) => state.homeDashboard.class_subject_info
  );
  const { ContentCommonAPIServiceV2ClientWithStatusCodeHandler } =
    useCommonServiceClientContext();
  const [loading, setLoading] = useState<'loading' | 'completed' | 'error'>(
    'loading'
  );
  const [chapterInfo, setChapterInfo] = useState<FetchSubjectChapterInfoV2>();

  useEffect(() => {
    setSelectedFunction(() => () => navigate(HOMEWORK_MANAGE));
    return () => {
      setSelectedFunction(null);
    };
  }, []);

  useEffect(() => {
    if (!subject_id) return;
    (async () => {
      try {
        setLoading('loading');
        const response =
          await ContentCommonAPIServiceV2ClientWithStatusCodeHandler.fetchSubjectChapterInfoV2(
            {
              personId: BigInt(teacher_id),
              personType: ProfileRolesEnum.PROFILE_ROLE_TEACHER,
              subjectId: Number(subject_id),
              bookId: class_subject_info?.bookId,
              isAssessFlow: true,
            }
          );
        setChapterInfo(response?.data);
        setLoading('completed');
      } catch (err) {
        console.log(err);
        setLoading('error');
      }
    })();
  }, [subject_id]);

  const chapters = [...(chapterInfo?.chapters ?? [])].sort(
    (a, b) => a.chapterNo - b.chapterNo
  );

  return (
    <Box sx={styles.root}>
      <Box sx={styles.textWithBadge}>
        <Typography variant="h2">
          Choose Chapter for Learning Homework
        </Typography>
        <ChipBadge label={chapters.length} color="primary" size="small" />
      </Box>
      {loading === 'loading' ? (
        <Loader />
      ) : loading === 'error' ? (
        <NoContentCard
          variant="error"
          icon="error"
          text="Could not load chapters. Please try again."
        />
      ) : chapters.length === 0 ? (
        <NoContentCard variant="info" icon="cards" text="No Chapters to show" />
      ) : (
        <DashboardGrid
          WebNoOfCols={3}
          mobileNoOfCols={1}
          items={chapters.map((elem) => (
            <ChapterCard
              key={elem.chapterId}
              withArrow
              variant="normal"
              image={getMediaBasePath(
                elem.posterImagesUrl,
                'processedMediaBucket'
              )}
              mainHeading={elem.chapterNo + '. ' + elem.chapterTitle}
              blueSubText={`${elem.noOfTopics} topics`}
              rootStyle={{ width: '100%' }}
              cardClickHandler={() =>
                navigate(
                  `${LEARNING_HOMEWORK_CREATE}/${subject_id}/${elem.chapterId}`
                )
              }
            />
          ))}
        />
      )}
    </Box>
  );
}
